import React, { useId, useMemo } from "react";
import {
  addDays,
  addMonths,
  browserTimeZone,
  describeDate,
  todayDate,
} from "./dateTimeParts";
import "./datetime.css";

const PRESETS = {
  today: { label: "Today", days: 0 },
  tomorrow: { label: "Tomorrow", days: 1 },
  "+7d": { label: "+1 week", days: 7 },
  "+14d": { label: "+2 weeks", days: 14 },
  "+30d": { label: "+30 days", days: 30 },
  "+90d": { label: "+90 days", days: 90 },
  "-7d": { label: "Last 7 days", days: -7 },
  "-30d": { label: "Last 30 days", days: -30 },
  "-90d": { label: "Last 90 days", days: -90 },
  "-1y": { label: "Last year", months: -12 },
};

/**
 * A date-only picker with the same echo/validation affordances as
 * {@link DateTimeField}. `value` is `"YYYY-MM-DD"`, which is what both the
 * native input and the APIs' date filters already use, so no conversion is
 * needed on either side.
 */
const DateField = ({
  id,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  min,
  max,
  minMessage,
  maxMessage,
  presets = [],
  allowClear = false,
  hint,
  error,
}) => {
  const reactId = useId();
  const fieldId = id || `ksdt-${reactId}`;

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    const today = todayDate(browserTimeZone());
    onChange(
      preset.months
        ? addMonths(today, preset.months)
        : addDays(today, preset.days),
    );
  };

  const validation = useMemo(() => {
    if (!value) return "";
    if (min && value < min) {
      return `Must be on or after ${minMessage || describeDate(min)}.`;
    }
    if (max && value > max) {
      return `Must be on or before ${maxMessage || describeDate(max)}.`;
    }
    return "";
  }, [value, min, max, minMessage, maxMessage]);

  const message = error || validation;

  return (
    <div className={`ksdt${message ? " is-invalid" : ""}`}>
      <label className="ksdt__label" htmlFor={fieldId}>
        {label}
        {required && (
          <span className="ksdt__req" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className="ksdt__controls">
        <input
          id={fieldId}
          className="ksdt__date"
          type="date"
          value={value || ""}
          min={min || undefined}
          max={max || undefined}
          disabled={disabled}
          required={required}
          aria-invalid={message ? true : undefined}
          aria-describedby={`${fieldId}-echo`}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      {(presets.length > 0 || (allowClear && value)) && (
        <div className="ksdt__presets">
          {presets.map((key) => (
            <button
              key={key}
              type="button"
              className="ksdt__preset"
              disabled={disabled}
              onClick={() => applyPreset(key)}
            >
              {PRESETS[key]?.label || key}
            </button>
          ))}
          {allowClear && value && (
            <button
              type="button"
              className="ksdt__preset"
              disabled={disabled}
              onClick={() => onChange("")}
            >
              Clear
            </button>
          )}
        </div>
      )}

      <div
        className={message ? "ksdt__error" : "ksdt__hint"}
        id={`${fieldId}-echo`}
      >
        {message || (value ? describeDate(value) : hint || "")}
      </div>
    </div>
  );
};

export default DateField;
